/**
 * V167.7 — Programmatic SEO audit landing page.
 *
 * Route: /audit/:slug — public, SEO-optimized, one page per indexed e-shop.
 * Pre-rendered server-side data from /api/audit-pages/:slug.
 *
 * Strategy: target long-tail queries like "alza.sk recenzia", "mall.sk seo audit",
 * "kde robí chyby [eshop]". Each page funnels traffic to /vyzva-5-dni and
 * /diagnostika-eshopu.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle, Eye, TrendingDown, ShieldAlert, ArrowRight, CheckCircle2, Loader2,
} from "lucide-react";
import { API } from "../config";

const CATEGORY = {
  visibility: { label: "Viditeľnosť (SEO / GEO)",       Icon: Eye,          color: "text-amber-300" },
  conversion: { label: "Konverzia (košík / dôvera)",    Icon: TrendingDown, color: "text-red-300" },
  ops:        { label: "Prevádzka (admin / GDPR)",      Icon: ShieldAlert,  color: "text-violet-300" },
};

export default function AuditPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/audit-pages/${encodeURIComponent(slug)}`)
      .then((r) => setPage(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"));
  }, [slug]);

  // Set <title> + meta description for SEO
  useEffect(() => {
    if (page) {
      document.title = page.title_seo || `${page.domain} — SEO audit | TopRankPulse`;
      let m = document.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement("meta");
        m.name = "description";
        document.head.appendChild(m);
      }
      m.content = page.meta_description || "";
    }
  }, [page]);

  if (err) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 text-center">
        <div>
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4"/>
          <h1 className="text-2xl font-black mb-2">Audit stránka neexistuje</h1>
          <p className="text-white/60 text-sm mb-5">Skús diagnostiku pre vlastný e-shop.</p>
          <Link to="/diagnostika-eshopu" className="px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold inline-flex items-center gap-2">
            Spustiť diagnostiku <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400"/>
      </div>
    );
  }

  const dominantDim = Object.entries(page.scores || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "conversion";
  const dominantFindings = (page.findings?.[dominantDim] || []).slice(0, 5);
  const totalScore = Math.round(
    ((page.scores?.visibility || 0) + (page.scores?.conversion || 0) + (page.scores?.ops || 0)) / 3
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5 px-6 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-white font-black text-base tracking-tight">
          <span className="text-emerald-400">▲</span> TopRankPulse
        </Link>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10" data-testid="audit-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
            <AlertTriangle className="w-3 h-3"/> Nezávislý SEO audit
          </div>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.05] mb-3">
            SEO audit: <span className="text-amber-300">{page.domain}</span>
          </h1>
          <p className="text-white/60 text-base sm:text-lg">
            Heuristický automatizovaný audit. Skóre 0-100 (vyššie = viac problémov).
            Spravené {(page.updated_at || "").slice(0, 10)}.
          </p>
        </div>

        {/* Score cards */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-10" data-testid="audit-scores">
          {Object.entries(page.scores || {}).map(([dim, score]) => {
            const meta = CATEGORY[dim];
            const isDominant = dim === dominantDim;
            return (
              <div
                key={dim}
                className={`rounded-xl border p-3 sm:p-4 ${isDominant ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-white/[0.02]"}`}
                data-testid={`audit-score-${dim}`}
              >
                {meta && <meta.Icon className={`w-4 h-4 mb-2 ${meta.color}`}/>}
                <div className="text-xs text-white/50 mb-1">{meta?.label || dim}</div>
                <div className={`text-2xl sm:text-3xl font-black ${isDominant ? "text-red-300" : "text-white/80"}`}>
                  {score}<span className="text-sm text-white/40">/100</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dominant findings */}
        {dominantFindings.length > 0 && (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 sm:p-6 mb-10" data-testid="audit-findings">
            <h2 className="text-lg sm:text-xl font-black mb-3 flex items-center gap-2">
              {CATEGORY[dominantDim]?.Icon && (
                <span className={CATEGORY[dominantDim].color}>
                  {(() => { const I = CATEGORY[dominantDim].Icon; return <I className="w-5 h-5"/>; })()}
                </span>
              )}
              TOP problémy: {CATEGORY[dominantDim]?.label}
            </h2>
            <ul className="space-y-2">
              {dominantFindings.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-white/80">
                  <span className="text-red-400 font-bold mt-0.5">{i + 1}.</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Long-form SEO body — auto-generated per page */}
        {page.enriched_content ? (
          <EnrichedBody enriched={page.enriched_content} domain={page.domain} totalScore={totalScore} dominantDim={dominantDim}/>
        ) : (
          <div className="prose prose-invert max-w-none mb-10 space-y-5 text-white/75">
            <h2 className="text-2xl font-black text-white">Čo tento audit hovorí o {page.domain}</h2>
            <p>
              Náš nezávislý AI sken e-shopu <strong>{page.domain}</strong> ohodnotil 3 dimenzie —
              <strong> viditeľnosť, konverziu a prevádzku</strong> — a vrátil priemerné pain skóre <strong>{totalScore}/100</strong>.
              Vyššie skóre znamená viac mechanických problémov, ktoré sa dajú vyriešiť agentom alebo nástrojom.
            </p>
            <h3 className="text-xl font-black text-white">Najslabšia dimenzia</h3>
            <p>
              Pre <strong>{page.domain}</strong> sme identifikovali <strong>{CATEGORY[dominantDim]?.label || dominantDim}</strong> ako kategóriu s najvyšším pain skóre ({page.scores?.[dominantDim] || 0}/100).
              To znamená, že e-shop tu stráca najviac potenciálnej hodnoty — buď preto, že ho ľudia nenájdu (visibility),
              alebo ho nájdu ale neprekliknú (conversion), alebo strácajú peniaze v administratíve (ops).
            </p>
            <h3 className="text-xl font-black text-white">Čo s tým môžeš urobiť (aj keď nie si majiteľ)</h3>
            <p>
              Ak nie si majiteľ <strong>{page.domain}</strong>, tento audit ti slúži ako orientačný benchmark.
              Spusti náš <Link to="/diagnostika-eshopu" className="text-emerald-400 hover:text-emerald-300 underline">bezplatný 60-sekundový diagnostický sken</Link> svojho vlastného e-shopu a porovnaj.
              Ak <em>si</em> majiteľ a chceš tento profil opraviť, máme 3 cesty:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>5-dňová bezplatná výzva, ktorá ťa prevedie každou dimenziou</li>
              <li>Diagnostický scan + bundle 3 agentov s 50 % zľavou na 3 mesiace</li>
              <li>Vlastný account a manuálne nastavenie agentov</li>
            </ul>
          </div>
        )}

        {/* CTA block */}
        <div className="bg-gradient-to-br from-emerald-500/15 to-transparent border border-emerald-500/30 rounded-2xl p-6 sm:p-8 mb-6 text-center" data-testid="audit-cta">
          <h2 className="text-2xl sm:text-3xl font-black mb-2">Chceš taký audit pre svoj e-shop?</h2>
          <p className="text-white/60 text-base mb-5 max-w-xl mx-auto">
            60-sekundový scan, výsledok hneď, žiadna kreditka.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/diagnostika-eshopu"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm sm:text-base transition-colors"
              data-testid="audit-cta-diagnostic"
            >
              Spustiť diagnostiku <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link
              to="/vyzva-5-dni"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-sm sm:text-base transition-colors"
              data-testid="audit-cta-challenge"
            >
              5-dňová výzva (zdarma) <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>

        <p className="text-xs text-white/40 text-center max-w-xl mx-auto">
          <CheckCircle2 className="w-3 h-3 inline mr-1"/>
          Tento audit nie je oficiálnym hodnotením {page.domain}. Generovaný automatizovane na základe verejne dostupných dát.
        </p>
      </article>

      <footer className="border-t border-white/5 px-6 py-8 text-center text-white/30 text-xs font-mono">
        © TopRankPulse · Programmatic SEO Audit · /audit/{page.slug}
      </footer>
    </div>
  );
}


function Paragraphs({ text }) {
  if (!text) return null;
  const parts = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-3 text-white/75 leading-relaxed">
      {parts.map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}

function EnrichedBody({ enriched, domain }) {
  return (
    <div className="space-y-10 mb-10" data-testid="audit-enriched">
      {enriched.intro && (
        <section>
          <h2 className="text-2xl font-black text-white mb-3">O e-shope {domain}</h2>
          <Paragraphs text={enriched.intro}/>
        </section>
      )}
      {enriched.visibility_analysis && (
        <section>
          <h2 className="text-2xl font-black text-amber-300 mb-3">Viditeľnosť — čo to znamená v praxi</h2>
          <Paragraphs text={enriched.visibility_analysis}/>
        </section>
      )}
      {enriched.conversion_analysis && (
        <section>
          <h2 className="text-2xl font-black text-red-300 mb-3">Konverzia — kde tečú peniaze</h2>
          <Paragraphs text={enriched.conversion_analysis}/>
        </section>
      )}
      {enriched.ops_analysis && (
        <section>
          <h2 className="text-2xl font-black text-violet-300 mb-3">Prevádzka — administratíva a compliance</h2>
          <Paragraphs text={enriched.ops_analysis}/>
        </section>
      )}
      {enriched.competitive_angle && (
        <section>
          <h2 className="text-2xl font-black text-white mb-3">Porovnanie s konkurenciou</h2>
          <Paragraphs text={enriched.competitive_angle}/>
        </section>
      )}
      {enriched.what_to_do_now && (
        <section>
          <h2 className="text-2xl font-black text-emerald-300 mb-3">Konkrétne kroky čo robiť teraz</h2>
          <Paragraphs text={enriched.what_to_do_now}/>
        </section>
      )}
      {Array.isArray(enriched.faq) && enriched.faq.length > 0 && (
        <section>
          <h2 className="text-2xl font-black text-white mb-4">Časté otázky</h2>
          <div className="space-y-3">
            {enriched.faq.map((item, i) => (
              <details key={i} className="bg-white/[0.02] border border-white/10 rounded-lg p-4">
                <summary className="font-bold text-white cursor-pointer">{item.q}</summary>
                <p className="text-white/70 mt-2 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
